import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  Package, Search, ClipboardList, X, User, ChevronDown
} from 'lucide-react';
import LoadingSpinner from '../../ui/LoadingSpinner';
import {
  getWithdrawnItems,
  reserveItemForJob,
  issueReservedItem,
  returnItem,
  markMovementAsUsed,
  getInventoryItems,
  getWithdrawnItemsStats,
  getJobs
} from '../../../redux/api';
import { getWorkers } from '../../../redux/features/workerSlice';
import { getSpareParts } from '../../../redux/features/sparePartSlice';
import { getBusinessType } from '../../../utils/businessTypeUtils';
import { isOpenJobForReservation } from '../../../utils/monkeyRotation';
import { su } from '../../inventory/stockUi';
import NumberField, { parseNumber } from '../../inventory/NumberField';

const STATUS_LABELS = {
  RESERVED: 'Rezervisano',
  ISSUED: 'Izdata',
  RETURNED: 'Vraćeno',
  USED: 'Iskorišćeno',
  CANCELLED: 'Otkazano'
};

const STATUS_CLASSES = {
  RESERVED: 'bg-yellow-900 text-yellow-300',
  ISSUED: 'bg-green-900 text-green-300',
  RETURNED: 'bg-blue-900 text-blue-300',
  USED: 'bg-purple-900 text-purple-300',
  CANCELLED: 'bg-gray-700 text-gray-300'
};

const getJobAddress = (job) => (job?.clientAddress || job?.clientName || '').trim() || 'Bez adrese';

const formatJobAddressLabel = (job) => {
  const date = new Date(job.serviceDate || job.createdAt).toLocaleDateString('sr-RS');
  return `${getJobAddress(job)} (${date})`;
};

const jobMatchesSearch = (job, query) => {
  const tokens = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (!tokens.length) return true;
  const haystack = [
    job.clientAddress,
    job.clientName,
    job.clientPhone,
    job.issueDescription,
    job.deviceType,
    job.deviceBrand,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return tokens.every((token) => haystack.includes(token));
};

const sortJobsByAddress = (jobList) =>
  [...jobList].sort((a, b) =>
    getJobAddress(a).localeCompare(getJobAddress(b), 'sr', { sensitivity: 'base' })
  );

const JobSearchDropdown = ({
  value,
  jobs,
  onChange,
  allowAll = false,
  placeholder,
  className = 'relative w-44 shrink-0',
  menuAlign = 'right',
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const rootRef = useRef(null);
  const searchRef = useRef(null);

  const sortedJobs = useMemo(() => sortJobsByAddress(jobs), [jobs]);

  useEffect(() => {
    if (!open) return undefined;
    const handleClickOutside = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  useEffect(() => {
    if (open) searchRef.current?.focus();
  }, [open]);

  const hasSelection = allowAll ? value !== 'all' : Boolean(value);
  const selectedJob = hasSelection ? sortedJobs.find((j) => j._id === value) : null;
  const defaultPlaceholder = allowAll
    ? t('jobs.jobsLabel', 'Poslovi')
    : t('inventory.withdrawn.searchJobsByAddress', 'Pretraži po adresi...');
  const buttonLabel = selectedJob ? formatJobAddressLabel(selectedJob) : (placeholder || defaultPlaceholder);

  const query = search.trim().toLowerCase();
  const filteredJobs = query
    ? sortedJobs.filter((job) => jobMatchesSearch(job, query))
    : sortedJobs;

  const handleSelect = (jobId) => {
    onChange(jobId);
    setOpen(false);
    setSearch('');
  };

  const menuPositionClass = menuAlign === 'left' ? 'left-0' : 'right-0';

  return (
    <div ref={rootRef} className={className}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={`${su.input} w-full text-left flex items-center justify-between gap-1 ${
          !selectedJob ? 'text-gray-400' : ''
        }`}
        title={buttonLabel}
      >
        <span className="truncate">{buttonLabel}</span>
        <ChevronDown className={`h-3.5 w-3.5 shrink-0 text-gray-400 transition ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div
          className={`absolute ${menuPositionClass} top-full z-50 mt-1 flex w-full min-w-[18rem] max-h-80 flex-col overflow-hidden rounded-md border border-gray-600 bg-gray-800 shadow-lg`}
        >
          <div className="border-b border-gray-700 p-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
              <input
                ref={searchRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('inventory.withdrawn.searchJobsByAddress', 'Pretraži po adresi, imenu...')}
                className="h-8 w-full rounded-md border border-gray-600 bg-gray-900/70 pl-8 pr-2 text-xs text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
          </div>
          <ul className="flex-1 overflow-y-auto py-1">
            {allowAll && (
              <li>
                <button
                  type="button"
                  onClick={() => handleSelect('all')}
                  className={`w-full px-3 py-2 text-left text-xs hover:bg-gray-700 ${
                    value === 'all' ? 'bg-gray-700/80 text-white' : 'text-gray-300'
                  }`}
                >
                  {t('jobs.allJobs', 'Svi poslovi')}
                </button>
              </li>
            )}
            {filteredJobs.map((job) => {
              const issue = job.issueDescription?.trim();
              return (
                <li key={job._id}>
                  <button
                    type="button"
                    onClick={() => handleSelect(job._id)}
                    className={`w-full px-3 py-2 text-left hover:bg-gray-700 ${
                      value === job._id ? 'bg-gray-700/80' : ''
                    }`}
                  >
                    <div className={`text-xs truncate ${value === job._id ? 'text-white' : 'text-gray-200'}`}>
                      {getJobAddress(job)}
                    </div>
                    <div className="text-[10px] text-gray-500 truncate mt-0.5">
                      {new Date(job.serviceDate || job.createdAt).toLocaleDateString('sr-RS')}
                      {issue ? ` · ${issue.substring(0, 60)}${issue.length > 60 ? '…' : ''}` : ''}
                    </div>
                  </button>
                </li>
              );
            })}
            {filteredJobs.length === 0 && (
              <li className="px-3 py-2 text-xs text-gray-500">
                {t('common.noResults', 'Nema rezultata')}
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

const WithdrawnItemsView = ({ fixedSourceType = null, stockKind = null }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { workers } = useSelector((state) => state.worker || { workers: [] });
  const { items: spareParts } = useSelector((state) => state.spareParts || { items: [] });

  const isSparePartsOnly = fixedSourceType === 'SPARE_PART';
  const isAssetStock = stockKind === 'ASSET';
  const isWarehouseOnly = fixedSourceType === 'INVENTORY_ITEM' && !isAssetStock;
  const showSourceToggle = !fixedSourceType;
  const inventoryStockKind = stockKind || 'WAREHOUSE';

  const [movements, setMovements] = useState([]);
  const [items, setItems] = useState([]);
  const [stockPickSource, setStockPickSource] = useState(
    isSparePartsOnly ? 'SPARE_PART' : 'INVENTORY_ITEM'
  );
  const [jobs, setJobs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [jobFilter, setJobFilter] = useState('all');
  const [issueModal, setIssueModal] = useState(null);
  const [returnModal, setReturnModal] = useState(null);
  const [issueWorkerId, setIssueWorkerId] = useState('');
  const [returnQuantity, setReturnQuantity] = useState(0);
  const [actionLoading, setActionLoading] = useState(false);
  const [formData, setFormData] = useState({
    jobId: '',
    itemId: '',
    sparePartId: '',
    sourceType: isSparePartsOnly ? 'SPARE_PART' : 'INVENTORY_ITEM',
    quantity: '',
    unitSellingPrice: '',
    notes: ''
  });

  const effectiveSource = fixedSourceType || stockPickSource;

  useEffect(() => {
    dispatch(getWorkers());
    if (!isWarehouseOnly) {
      dispatch(getSpareParts());
    }
  }, [dispatch, isWarehouseOnly]);

  useEffect(() => {
    fetchData();
  }, [jobFilter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const businessType = getBusinessType();
      const params = {};
      if (jobFilter !== 'all') params.jobId = jobFilter;
      if (fixedSourceType) params.sourceType = fixedSourceType;
      if (fixedSourceType === 'INVENTORY_ITEM' && stockKind) params.stockKind = stockKind;

      const statsParams = jobFilter !== 'all' ? { jobId: jobFilter } : {};
      if (fixedSourceType) statsParams.sourceType = fixedSourceType;
      if (fixedSourceType === 'INVENTORY_ITEM' && stockKind) statsParams.stockKind = stockKind;

      const requests = [
        getWithdrawnItems(params),
        getWithdrawnItemsStats(statsParams),
        businessType ? getJobs(businessType) : Promise.resolve({ data: [] }),
      ];
      if (!isSparePartsOnly) {
        requests.splice(1, 0, getInventoryItems({ stockKind: inventoryStockKind }));
      }

      const results = await Promise.all(requests);
      const movementsRes = results[0];
      const itemsRes = isSparePartsOnly ? { data: [] } : results[1];
      const statsRes = isSparePartsOnly ? results[1] : results[2];
      const jobsRes = isSparePartsOnly ? results[2] : results[3];

      setMovements(movementsRes.data || []);
      setItems(itemsRes.data || []);
      setStats(statsRes.data);
      setJobs((jobsRes.data || []).filter(isOpenJobForReservation));
    } catch (error) {
      toast.error(error.response?.data?.message || 'Greška pri učitavanju podataka');
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;

    if (name === 'itemId') {
      const item = items.find((i) => String(i._id) === value);
      setFormData((prev) => ({
        ...prev,
        itemId: value,
        unitSellingPrice:
          item?.sellingPrice != null && item.sellingPrice !== ''
            ? String(item.sellingPrice)
            : '',
      }));
      return;
    }

    if (name === 'sparePartId') {
      const part = spareParts.find((p) => String(p._id) === value);
      setFormData((prev) => ({
        ...prev,
        sparePartId: value,
        unitSellingPrice:
          part?.price != null && part.price !== '' ? String(part.price) : '',
      }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleReserve = async (e) => {
    e.preventDefault();
    if (!formData.jobId) {
      toast.error(t('inventory.withdrawn.jobRequired', 'Izaberite posao'));
      return;
    }
    const quantity = parseNumber(formData.quantity);
    if (!quantity || quantity <= 0) {
      toast.error(t('inventory.withdrawn.quantityRequired', 'Unesite količinu veću od 0'));
      return;
    }

    if (quantityExceedsStock) {
      toast.error(quantityError);
      return;
    }

    const unitSellingPrice =
      formData.unitSellingPrice === '' ? undefined : parseNumber(formData.unitSellingPrice);

    try {
      const payload =
        effectiveSource === 'SPARE_PART'
          ? {
              ...formData,
              sourceType: 'SPARE_PART',
              sparePartId: formData.sparePartId || formData.itemId,
              quantity,
              unitSellingPrice,
            }
          : {
              ...formData,
              sourceType: 'INVENTORY_ITEM',
              itemId: formData.itemId,
              quantity,
              unitSellingPrice,
            };
      await reserveItemForJob(payload);
      toast.success(
        isSparePartsOnly
          ? t('spareParts.withdrawn.reservedSuccess', 'Deo je povučen za posao')
          : isAssetStock
            ? t('assets.withdrawn.reservedSuccess', 'Sredstvo je povučeno za posao')
            : 'Stavka je rezervisana za posao'
      );
      setShowForm(false);
      setFormData({
        jobId: '',
        itemId: '',
        sparePartId: '',
        sourceType: isSparePartsOnly ? 'SPARE_PART' : 'INVENTORY_ITEM',
        quantity: '',
        unitSellingPrice: '',
        notes: ''
      });
      if (showSourceToggle) {
        setStockPickSource('INVENTORY_ITEM');
      }
      fetchData();
      if (!isWarehouseOnly) {
        dispatch(getSpareParts());
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Greška pri rezervaciji');
    }
  };

  const openIssueModal = (movement) => {
    setIssueModal(movement);
    setIssueWorkerId('');
  };

  const handleIssue = async () => {
    if (!issueModal) return;
    try {
      setActionLoading(true);
      await issueReservedItem(issueModal._id, { workerId: issueWorkerId || undefined });
      toast.success('Artikal je izdat');
      setIssueModal(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Greška pri izdavanju');
    } finally {
      setActionLoading(false);
    }
  };

  const openReturnModal = (movement) => {
    setReturnModal(movement);
    setReturnQuantity(movement.quantity);
  };

  const handleReturn = async () => {
    if (!returnModal) return;
    try {
      setActionLoading(true);
      await returnItem(returnModal._id, { quantity: returnQuantity });
      toast.success('Artikal je vraćen');
      setReturnModal(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Greška pri vraćanju');
    } finally {
      setActionLoading(false);
    }
  };

  const handleMarkUsed = async (movementId) => {
    try {
      await markMovementAsUsed(movementId);
      toast.success('Artikal je označen kao iskorišćen');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Greška pri označavanju');
    }
  };

  const getMovementItemLabel = (movement) => {
    if (movement.stockItem?.name) {
      const code = movement.stockItem.code || movement.stockItem.itemCode;
      return `${movement.stockItem.name}${code ? ` (${code})` : ''}`;
    }
    if (movement.sourceType === 'SPARE_PART' && movement.sparePartId) {
      const sp = typeof movement.sparePartId === 'object' ? movement.sparePartId : null;
      return sp ? `${sp.name}${sp.code ? ` (${sp.code})` : ''}` : 'Rezervni deo';
    }
    if (movement.itemId) {
      const item = typeof movement.itemId === 'object' ? movement.itemId : null;
      return item ? `${item.name}${item.itemCode ? ` (${item.itemCode})` : ''}` : 'Artikal';
    }
    return '-';
  };

  const getMovementSourceLabel = (movement) =>
    movement.sourceType === 'SPARE_PART' ? 'Rez. delovi' : 'Magacin';

  const getReservedSpareQty = useCallback(
    (partId) => {
      if (!partId) return 0;
      return movements
        .filter(
          (m) =>
            m.status === 'RESERVED' &&
            m.sourceType === 'SPARE_PART' &&
            String(m.sparePartId?._id || m.sparePartId) === String(partId)
        )
        .reduce((sum, m) => sum + Number(m.quantity || 0), 0);
    },
    [movements]
  );

  const selectedInventoryItem = useMemo(
    () =>
      effectiveSource === 'INVENTORY_ITEM'
        ? items.find((i) => String(i._id) === formData.itemId)
        : null,
    [effectiveSource, items, formData.itemId]
  );

  const selectedSparePart = useMemo(
    () =>
      effectiveSource === 'SPARE_PART'
        ? spareParts.find((p) => String(p._id) === formData.sparePartId)
        : null,
    [effectiveSource, spareParts, formData.sparePartId]
  );

  const getSparePartAvailableQty = useCallback(
    (part) => {
      if (!part) return 0;
      return Math.max(0, Number(part.quantity || 0) - getReservedSpareQty(part._id));
    },
    [getReservedSpareQty]
  );

  const availableStockQuantity = useMemo(() => {
    if (selectedInventoryItem) {
      return Number(selectedInventoryItem.availableQuantity || 0);
    }
    if (selectedSparePart) {
      const onHand = Number(selectedSparePart.quantity || 0);
      return Math.max(0, onHand - getReservedSpareQty(selectedSparePart._id));
    }
    return null;
  }, [selectedInventoryItem, selectedSparePart, getReservedSpareQty]);

  const stockUnit =
    selectedInventoryItem?.unit || (selectedSparePart ? 'kom' : '');

  const requestedQuantity = parseNumber(formData.quantity, NaN);

  const quantityExceedsStock =
    availableStockQuantity != null &&
    Number.isFinite(requestedQuantity) &&
    requestedQuantity > 0 &&
    requestedQuantity > availableStockQuantity;

  const quantityError = quantityExceedsStock
    ? t('inventory.withdrawn.insufficientStock', 'Nedovoljna zaliha. Dostupno: {{available}} {{unit}}', {
        available: availableStockQuantity,
        unit: stockUnit,
      })
    : '';

  const canSubmitReserve =
    Boolean(formData.jobId) &&
    !quantityExceedsStock &&
    Number.isFinite(requestedQuantity) &&
    requestedQuantity > 0;

  const filteredMovements = movements.filter((m) => {
    const search = searchTerm.toLowerCase();
    const itemLabel = getMovementItemLabel(m).toLowerCase();
    return (
      itemLabel.includes(search) ||
      m.jobId?.clientName?.toLowerCase().includes(search) ||
      m.workerId?.firstName?.toLowerCase().includes(search) ||
      m.workerId?.lastName?.toLowerCase().includes(search)
    );
  });

  const formatCurrency = (value) =>
    new Intl.NumberFormat('sr-RS', { style: 'currency', currency: 'RSD' }).format(value || 0);

  if (loading) {
    return (
      <div className={`${su.page} flex items-center justify-center`}>
        <LoadingSpinner size="lg" color="white" />
      </div>
    );
  }

  return (
    <div className={su.page}>
      <div className={su.wrap}>
        <div className={`${su.card} ${su.cardPad}`}>
          <div className={su.toolbar}>
            <div className="min-w-0">
              <h1 className={su.title}>
                <ClipboardList className={su.icon} />
                {isSparePartsOnly
                  ? t('spareParts.withdrawn.title', 'Povučeni delovi')
                  : isAssetStock
                    ? t('assets.withdrawn.title', 'Povučena sredstva')
                    : t('inventory.withdrawn.title', 'Povučena roba')}
              </h1>
              {!isSparePartsOnly && !isAssetStock && (
                <p className={su.subtitle}>
                  {t(
                    'inventory.withdrawn.subtitle',
                    'Roba vezana za konkretan posao. Za terenski magacin radnika koristite Reversi.'
                  )}
                </p>
              )}
            </div>
            <div className={su.searchWrap}>
              <Search className={su.searchIcon} />
              <input
                type="text"
                placeholder={t('inventory.withdrawn.search', 'Pretraži...')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={su.searchInput}
              />
            </div>
            <JobSearchDropdown value={jobFilter} jobs={jobs} onChange={setJobFilter} allowAll />
            <button onClick={() => setShowForm(true)} className={su.btnPrimary}>
              <Package className="h-3.5 w-3.5" />
              {isSparePartsOnly
                ? t('spareParts.withdrawn.pull', 'Povuci deo')
                : isAssetStock
                  ? t('assets.withdrawn.pull', 'Povuci sredstvo')
                  : t('inventory.withdrawn.reserve', 'Rezerviši')}
            </button>
          </div>

          {stats && (
            <div className="mt-2.5">
              <div className={`${su.stat} inline-block min-w-[7rem]`}>
                <div className={su.statLabel}>{t('inventory.withdrawn.reserved', 'Rezervisano')}</div>
                <div className="text-sm font-semibold text-yellow-400 mt-0.5">{stats.totalReserved || 0}</div>
              </div>
            </div>
          )}
        </div>

        {showForm && (
          <div className="bg-gray-800/90 rounded-lg border border-gray-700/80 p-3">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-white">
                {isSparePartsOnly
                  ? t('spareParts.withdrawn.pullItem', 'Povuci deo za posao')
                  : isAssetStock
                    ? t('assets.withdrawn.pullItem', 'Povuci sredstvo za posao')
                    : t('inventory.withdrawn.reserveItem', 'Rezerviši artikal')}
              </h2>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-white">
                <X className="h-4 w-4" />
              </button>
            </div>
            <form onSubmit={handleReserve} className="space-y-2">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
                {showSourceToggle && (
                <div className="md:col-span-2">
                  <label className="block text-[11px] font-medium text-gray-400 mb-0.5">Skladište</label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setStockPickSource('INVENTORY_ITEM');
                        setFormData((prev) => ({
                          ...prev,
                          itemId: '',
                          sparePartId: '',
                          unitSellingPrice: '',
                        }));
                      }}
                      className={`flex-1 h-8 rounded-md text-xs ${
                        stockPickSource === 'INVENTORY_ITEM' ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300'
                      }`}
                    >
                      Magacin
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setStockPickSource('SPARE_PART');
                        setFormData((prev) => ({
                          ...prev,
                          itemId: '',
                          sparePartId: '',
                          unitSellingPrice: '',
                        }));
                      }}
                      className={`flex-1 h-8 rounded-md text-xs ${
                        stockPickSource === 'SPARE_PART' ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300'
                      }`}
                    >
                      Rezervni delovi
                    </button>
                  </div>
                </div>
                )}
                <div>
                  <label className="block text-[11px] font-medium text-gray-400 mb-0.5">{t('jobs.job', 'Posao')} *</label>
                  <JobSearchDropdown
                    value={formData.jobId}
                    jobs={jobs}
                    onChange={(jobId) => setFormData((prev) => ({ ...prev, jobId }))}
                    placeholder={t('common.select', 'Izaberi posao...')}
                    className="relative w-full"
                    menuAlign="left"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-gray-400 mb-0.5">
                    {effectiveSource === 'SPARE_PART' ? 'Rezervni deo' : isAssetStock ? 'Sredstvo' : t('inventory.name', 'Artikal')} *
                  </label>
                  {effectiveSource === 'INVENTORY_ITEM' ? (
                  <select
                    name="itemId"
                    value={formData.itemId}
                    onChange={handleFormChange}
                    required
                    className={su.input}
                  >
                    <option value="">{t('common.select', 'Izaberi...')}</option>
                    {items.filter((item) => item.availableQuantity > 0).map((item) => (
                      <option key={item._id} value={item._id}>
                        {item.itemCode} - {item.name} (Dostupno: {item.availableQuantity} {item.unit})
                      </option>
                    ))}
                  </select>
                  ) : (
                  <select
                    name="sparePartId"
                    value={formData.sparePartId}
                    onChange={handleFormChange}
                    required
                    className={su.input}
                  >
                    <option value="">{t('common.select', 'Izaberi...')}</option>
                    {spareParts.filter((part) => getSparePartAvailableQty(part) > 0).map((part) => (
                      <option key={part._id} value={part._id}>
                        {part.code ? `${part.code} - ` : ''}{part.name} (Dostupno: {getSparePartAvailableQty(part)})
                      </option>
                    ))}
                  </select>
                  )}
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-gray-400 mb-0.5">{t('inventory.quantity', 'Količina')} *</label>
                  <NumberField
                    name="quantity"
                    value={formData.quantity}
                    onChange={handleFormChange}
                    required
                    min="0.01"
                    step="0.01"
                    max={availableStockQuantity != null ? availableStockQuantity : undefined}
                    className={`${su.input}${quantityError ? ' border-red-500 focus:ring-red-500 focus:border-red-500' : ''}`}
                  />
                  {availableStockQuantity != null && (
                    <span className="text-[11px] text-gray-400 mt-0.5 block">
                      {t('inventory.available', 'Dostupno')}: {availableStockQuantity} {stockUnit}
                    </span>
                  )}
                  {quantityError && (
                    <span className="text-[11px] text-red-400 mt-0.5 block">{quantityError}</span>
                  )}
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-gray-400 mb-0.5">{t('inventory.sellingPrice', 'Prodajna cena')}</label>
                  <NumberField
                    name="unitSellingPrice"
                    value={formData.unitSellingPrice}
                    onChange={handleFormChange}
                    step="0.01"
                    min="0"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 bg-gray-700 text-white rounded-lg">
                  {t('common.cancel', 'Otkaži')}
                </button>
                <button
                  type="submit"
                  disabled={!canSubmitReserve}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSparePartsOnly
                    ? t('spareParts.withdrawn.pull', 'Povuci deo')
                    : isAssetStock
                      ? t('assets.withdrawn.pull', 'Povuci sredstvo')
                      : t('inventory.withdrawn.reserve', 'Rezerviši')}
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="bg-gray-800/90 rounded-lg border border-gray-700/80 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className={su.tableHead}>
                <tr>
                  <th className="px-2.5 py-1.5 text-left text-[10px] font-medium text-gray-400 uppercase">{t('inventory.name', 'Stavka')}</th>
                  {showSourceToggle && (
                  <th className="px-2.5 py-1.5 text-left text-[10px] font-medium text-gray-400 uppercase">Skladište</th>
                  )}
                  <th className="px-2.5 py-1.5 text-left text-[10px] font-medium text-gray-400 uppercase">{t('jobs.client', 'Klijent')}</th>
                  <th className="px-2.5 py-1.5 text-left text-[10px] font-medium text-gray-400 uppercase">{t('nav.workers', 'Radnik')}</th>
                  <th className="px-2.5 py-1.5 text-left text-[10px] font-medium text-gray-400 uppercase">{t('inventory.quantity', 'Količina')}</th>
                  <th className="px-2.5 py-1.5 text-left text-[10px] font-medium text-gray-400 uppercase">{t('inventory.withdrawn.status', 'Status')}</th>
                  <th className="px-2.5 py-1.5 text-left text-[10px] font-medium text-gray-400 uppercase">{t('inventory.withdrawn.totalCost', 'Trošak')}</th>
                  <th className="px-2.5 py-1.5 text-left text-[10px] font-medium text-gray-400 uppercase">{t('inventory.sellingPrice', 'Prodajna cena')}</th>
                  <th className="px-2.5 py-1.5 text-left text-[10px] font-medium text-gray-400 uppercase">{t('common.actions', 'Akcije')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {filteredMovements.length === 0 ? (
                  <tr>
                    <td colSpan={showSourceToggle ? 9 : 8} className="px-3 py-6 text-center text-xs text-gray-500">
                      {isSparePartsOnly
                        ? t('spareParts.withdrawn.noMovements', 'Nema povučenih delova')
                        : isAssetStock
                          ? t('assets.withdrawn.noMovements', 'Nema povučenih sredstava')
                          : t('inventory.withdrawn.noMovements', 'Nema povučene robe')}
                    </td>
                  </tr>
                ) : (
                  filteredMovements.map((movement) => (
                    <tr key={movement._id} className="hover:bg-gray-700">
                      <td className="px-2.5 py-1.5 text-xs text-white">{getMovementItemLabel(movement)}</td>
                      {showSourceToggle && (
                      <td className="px-2.5 py-1.5 text-xs text-gray-400">{getMovementSourceLabel(movement)}</td>
                      )}
                      <td className="px-2.5 py-1.5 text-xs text-white">{movement.jobId?.clientName || '-'}</td>
                      <td className="px-2.5 py-1.5 text-xs text-white">
                        {movement.workerId
                          ? `${movement.workerId.firstName || ''} ${movement.workerId.lastName || ''}`.trim()
                          : '-'}
                      </td>
                      <td className="px-2.5 py-1.5 text-xs text-white">{movement.quantity} {movement.unit}</td>
                      <td className="px-4 py-3 text-sm">
                        <span className={`px-2 py-1 rounded text-xs ${STATUS_CLASSES[movement.status] || 'bg-gray-700 text-gray-300'}`}>
                          {STATUS_LABELS[movement.status] || movement.status}
                        </span>
                      </td>
                      <td className="px-2.5 py-1.5 text-xs text-white">{formatCurrency(movement.totalCost)}</td>
                      <td className="px-2.5 py-1.5 text-xs text-white">{formatCurrency(movement.totalSellingPrice)}</td>
                      <td className="px-4 py-3 text-sm">
                        <div className="flex flex-wrap gap-2">
                          {movement.status === 'RESERVED' && (
                            <button onClick={() => openIssueModal(movement)} className="text-green-400 hover:text-green-300 text-xs">
                              {t('inventory.withdrawn.issue', 'Izdaj')}
                            </button>
                          )}
                          {(movement.status === 'ISSUED' || movement.status === 'RESERVED') && (
                            <button onClick={() => openReturnModal(movement)} className="text-blue-400 hover:text-blue-300 text-xs">
                              {t('inventory.withdrawn.return', 'Vrati')}
                            </button>
                          )}
                          {movement.status === 'ISSUED' && (
                            <button onClick={() => handleMarkUsed(movement._id)} className="text-purple-400 hover:text-purple-300 text-xs">
                              {t('inventory.withdrawn.used', 'Iskorišćeno')}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {issueModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className={`${su.card} ${su.cardPad} w-full max-w-md`}>
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <User className="h-5 w-5" />
              {t('inventory.withdrawn.issueItem', 'Izdaj artikal')}
            </h3>
            <p className="text-sm text-gray-400 mb-2">
              {getMovementItemLabel(issueModal)} · {issueModal.quantity} {issueModal.unit}
            </p>
            <p className="text-xs text-gray-500 mb-4">
              Deo ostaje na istom poslu. Izdavanjem se skida sa stanja magacina i može se vezati za radnika koji ga nosi na teren.
            </p>
            <label className="block text-sm text-gray-300 mb-1">{t('nav.workers', 'Radnik')}</label>
            <select
              value={issueWorkerId}
              onChange={(e) => setIssueWorkerId(e.target.value)}
              className={`${su.input} mb-3`}
            >
              <option value="">{t('common.select', 'Izaberi radnika (opciono)...')}</option>
              {workers.map((worker) => (
                <option key={worker._id} value={worker._id}>
                  {worker.firstName} {worker.lastName}
                </option>
              ))}
            </select>
            <div className="flex justify-end gap-2">
              <button onClick={() => setIssueModal(null)} className="px-4 py-2 bg-gray-700 text-white rounded-lg">
                {t('common.cancel', 'Otkaži')}
              </button>
              <button onClick={handleIssue} disabled={actionLoading} className="px-4 py-2 bg-green-600 text-white rounded-lg disabled:opacity-50">
                {t('inventory.withdrawn.issue', 'Izdaj')}
              </button>
            </div>
          </div>
        </div>
      )}

      {returnModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className={`${su.card} ${su.cardPad} w-full max-w-md`}>
            <h3 className="text-lg font-bold text-white mb-4">
              {t('inventory.withdrawn.returnItem', 'Vrati artikal')}
            </h3>
            <p className="text-sm text-gray-400 mb-2">
              {getMovementItemLabel(returnModal)} · max {returnModal.quantity} {returnModal.unit}
            </p>
            <p className="text-xs text-gray-500 mb-4">
              Ako je izdato 10 a utrošeno 5, ovde vrati 5 — ostatak ostaje na poslu (pa ga označi kao iskorišćeno).
            </p>
            <label className="block text-sm text-gray-300 mb-1">{t('inventory.quantity', 'Količina')}</label>
            <input
              type="number"
              step="0.01"
              min="0.01"
              max={returnModal.quantity}
              value={returnQuantity}
              onChange={(e) => setReturnQuantity(parseFloat(e.target.value) || 0)}
              className={`${su.input} mb-3`}
            />
            <div className="flex justify-end gap-2">
              <button onClick={() => setReturnModal(null)} className="px-4 py-2 bg-gray-700 text-white rounded-lg">
                {t('common.cancel', 'Otkaži')}
              </button>
              <button onClick={handleReturn} disabled={actionLoading} className="px-4 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50">
                {t('inventory.withdrawn.return', 'Vrati')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WithdrawnItemsView;
